import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { PenLine, Send, UserRound, ExternalLink, Lock, BadgeCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_ROUTES } from "@/lib/types";
import { PageHeader } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { title: "", summary: "", body: "", hero_image_url: "", category: "crypto" };
const field = "mt-1 bg-[#1A1A1A] border-white/10 text-white";

const AuthorPreview = ({ a }: { a: any }) => {
  const initials = (a.display_name || "?").split(" ").map((w: string) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[#7C3AED]/30 bg-[#7C3AED]/[0.07] px-4 py-3"
      data-testid="waa-selected-author">
      {a.avatar_url ? (
        <img src={a.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover border border-white/15 shrink-0" />
      ) : (
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] flex items-center justify-center shrink-0">
          <span className="font-heading font-black text-white text-sm">{initials}</span>
        </div>
      )}
      <div className="min-w-0">
        <p className="text-white font-medium text-sm flex items-center gap-1.5">
          {a.display_name} <BadgeCheck className="h-3.5 w-3.5 text-green-400" />
        </p>
        <p className="font-mono text-[11px] text-[#00E5FF]">{a.handle ? `@${a.handle}` : "no handle yet"}</p>
      </div>
      {a.handle && (
        <Link to={`/author/${a.handle}`} target="_blank" data-testid="waa-author-public-page"
          className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] text-gray-400 hover:text-white transition-colors shrink-0">
          Byline page <ExternalLink className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
};

export default function WriteAsAuthor() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [authorId, setAuthorId] = useState(searchParams.get("author") || "");
  const [form, setForm] = useState(EMPTY);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState<any>(null);

  const isSuper = user?.role === "superadmin";
  const { data: authors } = useQuery({
    queryKey: ["admin-approved-authors"],
    enabled: isSuper,
    queryFn: async () => (await api.get("/admin/author-applications?status=approved")).data,
  });

  if (!isSuper) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-24 px-6" data-testid="waa-restricted">
        <span className="h-12 w-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-4">
          <Lock className="h-5 w-5 text-red-400" />
        </span>
        <h2 className="font-heading font-bold text-white text-lg">Super Admin only</h2>
        <p className="text-gray-400 text-sm mt-2 max-w-md">
          Publishing stories under an author's byline is restricted to the Super Admin.
        </p>
      </div>
    );
  }

  const selected = (authors || []).find((a: any) => a.id === authorId);
  const words = form.body.trim() ? form.body.trim().split(/\s+/).length : 0;
  const valid = !!authorId && form.title.length >= 10 && form.summary.length >= 20 && form.body.length >= 100;

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/author-articles", {
        application_id: authorId, title: form.title, summary: form.summary, body: form.body,
        hero_image_url: form.hero_image_url || null, categories: [form.category],
      });
      setPublished(data.article);
      toast.success(data.message);
      setForm(EMPTY);
    } catch (e) { toast.error(apiError(e)); }
    setPublishing(false);
  };

  return (
    <div data-testid="write-as-author-page">
      <PageHeader title="Write as Author"
        sub="Publish a story under any approved author's byline — it goes live instantly, no review queue." />

      {published && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-green-500/30 bg-green-500/[0.08] px-4 py-3"
          data-testid="waa-success-banner">
          <BadgeCheck className="h-4 w-4 text-green-400 shrink-0" />
          <p className="text-sm text-green-300 font-medium line-clamp-1">Published: {published.title}</p>
          <div className="ml-auto flex items-center gap-3 shrink-0">
            <Link to={`/article/${published.slug}`} target="_blank" data-testid="waa-view-article"
              className="inline-flex items-center gap-1 font-mono text-[11px] text-[#00E5FF] hover:text-white transition-colors">
              View live article <ExternalLink className="h-3 w-3" />
            </Link>
            <button type="button" onClick={() => setPublished(null)} data-testid="waa-write-another"
              className="font-mono text-[10px] uppercase text-gray-400 hover:text-white transition-colors">
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="max-w-3xl space-y-6">
        <div className="bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="waa-attribution-card">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-1.5 mb-3">
            <UserRound className="h-3.5 w-3.5 text-[#A78BFA]" /> Attribute to
          </p>
          <Select value={authorId} onValueChange={setAuthorId}>
            <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white" data-testid="waa-author-select">
              <SelectValue placeholder="Choose an approved author…" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white max-h-72">
              {(authors || []).map((a: any) => (
                <SelectItem key={a.id} value={a.id} data-testid={`waa-author-option-${a.id}`}>
                  {a.display_name}{a.handle ? ` · @${a.handle}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selected && <div className="mt-3"><AuthorPreview a={selected} /></div>}
          {authors && authors.length === 0 && (
            <p className="text-gray-500 text-sm mt-3" data-testid="waa-no-authors">
              No approved authors yet — approve or create one on the <Link to="/admin/authors" className="text-[#7C3AED] underline">Authors page</Link>.
            </p>
          )}
        </div>

        <div className="space-y-4 bg-[#121212] border border-white/10 rounded-xl p-6 sm:p-8" data-testid="waa-editor-card">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#7C3AED]">
            <PenLine className="h-4 w-4" /> Story editor
          </div>
          <div>
            <Label>Headline</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="A sharp, specific headline (10+ chars)"
              data-testid="waa-form-title" className={field} />
          </div>
          <div>
            <Label>Summary / dek</Label>
            <Textarea value={form.summary} rows={2}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              placeholder="One or two sentences that sell the story (20+ chars)"
              data-testid="waa-form-summary" className={field} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className={field} data-testid="waa-form-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  {CATEGORY_ROUTES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hero image URL (optional)</Label>
              <Input value={form.hero_image_url} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
                placeholder="https://…" data-testid="waa-form-image" className={field} />
            </div>
          </div>
          <div>
            <Label>Story body</Label>
            <Textarea value={form.body} rows={12}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Write the full story here. Blank lines create new paragraphs. (100+ chars)"
              data-testid="waa-form-body" className={`${field} font-body`} />
            <p className="font-mono text-[10px] text-gray-500 mt-1.5">
              {words ? `${words} words · ~${Math.max(1, Math.round(words / 220))} min read` : "0 words"}
            </p>
          </div>
          <Button onClick={publish} disabled={publishing || !valid} data-testid="waa-publish-button"
            className="bg-[#7C3AED] text-white font-bold hover:bg-[#6a2dd0]">
            <Send className="h-4 w-4 mr-1.5" />
            {publishing ? "Publishing…" : selected ? `Publish as ${selected.display_name}` : "Publish"}
          </Button>
        </div>
      </div>
    </div>
  );
}
